/** @format */

import {
	selectedAttributeAction,
	setAllSelectedAttributeOptions
} from '../+state/attributes.actions';

import { AttributesResolvedSkuService } from './attributes-resolved-sku.service';
import { AttributeOption } from '../interfaces/attributes-v2.interface';
import { Injectable } from '@angular/core';
import { ProductSku } from '@shared/data-access/interfaces';
import { Store } from '@ngrx/store';
import { selectDefiningAttributesLength } from '@pdp/data-access/state';
import { selectSelectedAttributeOptions } from '../+state/attributes.selectors';
import { take } from 'rxjs/operators';

@Injectable({
	providedIn: 'root'
})
export class AttributesAvailabilityService {
	public definingAttributesLengthForSkuResolve: number;
	constructor(
		private store: Store,
		private attributesResolvedSkuService: AttributesResolvedSkuService
	) {
		this.store
			.select(selectDefiningAttributesLength)
			.pipe(take(2))
			.subscribe((definingAttributeLength) => {
				this.definingAttributesLengthForSkuResolve = definingAttributeLength;
			});
	}

	checkSelectedAttributeAvailabilityAndResolvedSku(
		allProductSkus: ProductSku[],
		selectedAttribute: AttributeOption,
		alternativeInStockRecsEnabled = false
	) {
		const selectedAvailableSkusForPossibleResolve =
			this.filterThroughAllProductSkusToFindMatchesWithSelectedAttr(
				allProductSkus,
				selectedAttribute
			);

		this.setSelectedAttributeWithAvailableOptionsInState(
			selectedAvailableSkusForPossibleResolve,
			selectedAttribute,
			alternativeInStockRecsEnabled
		);

		this.attributesResolvedSkuService.checkForResolvedSku(
			selectedAvailableSkusForPossibleResolve,
			this.definingAttributesLengthForSkuResolve
		);
	}

	/**
	 *  @description go through ProductSkus and find all skus that have
	 *  the defining attribute that matches the selected Attribute Value
	 *  @example user clicks on size xl, all skus with definingAttributes xl will return
	 */
	filterThroughAllProductSkusToFindMatchesWithSelectedAttr(
		allProductSkus: ProductSku[],
		selectedAttribute: AttributeOption
	): ProductSku[] {
		return allProductSkus.filter((sku) => {
			let validAttributes = [];
			if (sku.definingAttributes) {
				// checking each defining attribute against selections for matches
				validAttributes = sku.definingAttributes.filter((attr) => {
					if (
						attr.value.toLowerCase() === selectedAttribute.value.toLowerCase() &&
						attr.name.toLowerCase() === selectedAttribute.name.toLowerCase()
					) {
						return true;
					}
					return false;
				});
				return validAttributes.length > 0;
			}
			return false;
		});
	}

	/**
	 *  @description compare available sku attributes with all sku attributes
	 *  and update the availableAttributeOptions property for the attributes in state store
	 */
	setSelectedAttributeWithAvailableOptionsInState(
		availableSkusForSelection: ProductSku[],
		selectedAttribute: AttributeOption,
		alternativeInStockRecsEnabled: boolean
	) {
		const availableAttributeOptions = [];
		// set all available options that are possible with the attribute selected.
		// Ex. Clicking on 'blue' find all attr options that are available with blue. availableAttributeOptions = ['s', 'm', 'l', '2E', 'medium'].
		availableSkusForSelection.forEach((availableSku) => {
			availableSku.definingAttributes.forEach((attributes) => {
				if (
					attributes.name.toLowerCase() !== selectedAttribute.name.toLowerCase() &&
					availableAttributeOptions.indexOf(attributes.name + attributes.value) === -1
				) {
					availableAttributeOptions.push(attributes.name + attributes.value);
				}
			});
		});
		// add available attribute options array to object with selected Attribute
		const updatedSelectedValueWithAvailableAttributes = { ...selectedAttribute };
		updatedSelectedValueWithAvailableAttributes.availableAttributeOptions =
			availableAttributeOptions;

		// set attribute selected in state with available options array to render the HTML.
		this.store.dispatch(
			selectedAttributeAction({
				attributeSelected: updatedSelectedValueWithAvailableAttributes
			})
		);
		this.setAllSelectedAttributeOptionsInState(
			updatedSelectedValueWithAvailableAttributes,
			alternativeInStockRecsEnabled
		);
	}

	/**
	 * @description adds current selection to selectedAttributeOptions Array.
	 * If disabled/unavailable attribute is selected remove previous selection from array
	 * @example a user clicks on a green shirt, this adds color green to array.
	 * If then a user clicks on a unavailable size xl this removes green from array
	 */
	setAllSelectedAttributeOptionsInState(
		selectedValueWithAvailableAttributes,
		alternativeInStockRecsEnabled
	) {
		let updatedAllSelectedAvailableAttrOptions = [];
		this.store
			.select(selectSelectedAttributeOptions)
			.pipe(take(1))
			.subscribe((attributeOptions) => {
				updatedAllSelectedAvailableAttrOptions = attributeOptions
					.filter((attr: AttributeOption) => {
						if (
							attr &&
							selectedValueWithAvailableAttributes.name.toLowerCase() !==
								attr.name.toLowerCase()
						) {
							if (alternativeInStockRecsEnabled) {
								return true;
							}
							// true if selected value includes attribute in available attributes, false otherwise
							return selectedValueWithAvailableAttributes.availableAttributeOptions.some(
								(availableAttribute: string) =>
									availableAttribute.toLowerCase() ===
									(attr.name + attr.value).toLowerCase()
							);
						}
					})
					.concat(selectedValueWithAvailableAttributes);
			});

		this.store.dispatch(
			setAllSelectedAttributeOptions({
				allSelectedAttributeOptions: updatedAllSelectedAvailableAttrOptions
			})
		);
	}
}
